// @givmo/app-charity — Claim status + Tier-2 upload (CPA-WEB2, plan A-1).
//
// A single claim's status screen. There is no caller-facing single-claim GET (self-scoped reads are
// the list + the per-claim documents), so we resolve THIS claim from `charityAdminListMyCharityClaims`
// (GET /charity-admin/claims) by id. Shown:
//   - the claim's translated status (submitted / pending review / approved / rejected + reason);
//   - for a PENDING_REVIEW / Tier-2 claim: the <DocumentUpload> flow (the IRS letter);
//   - TAKEOVER messaging: a claim that lands PENDING_REVIEW may be a takeover of an already-managed
//     org (a claim on a claimed org is ALWAYS Tier-2, even on a domain match — plan §3.5).
//
// ★ THE OUTCOME IS A CONTINUATION, NOT A RECEIPT (CPA-DP1, 2026-08-10). This is where a claimant lands
// the moment they submit, and for the document path it is the screen that decides whether the claim
// ever completes — yet it opened with a status pill, a freshness stamp, and a paragraph of doubt about
// whether the organization is already managed. The one thing the caller could actually DO was last.
// The rebuild reorders by what the reader can act on:
//   - the UPLOADS headline the pending screen, under a heading that names them as the remaining step;
//   - `claimReviewNote` — the plain-language "why is a person reading this", read off the claim's
//     `reason` (CPA-WEB91) — renders HERE, where the question is asked, instead of only on the apps
//     grid the caller has not reached yet;
//   - status and freshness are demoted below the action (nothing about them is actionable);
//   - the takeover paragraph is CONDITIONED. It used to assert the already-managed possibility to
//     every pending claimant, including the Tier-2 majority whose review has a different and already-
//     stated cause (the review note's). It now shows only where it is the live explanation —
//     a pending claim that is NOT Tier-2, i.e. one whose instant check should have cleared and did
//     not — and reads as one line rather than a card.
// No wire change: every branch above is decided from `tier`, `status` and `reason`, which the list
// already returns. Claim semantics are untouched.
//
// ★ CONTINUE WAITS FOR THE NEW MEMBERSHIP (CPA-WEB83, the wizard's sibling site). A reviewer's
// approval reaches this page on its CLAIMS read (the poll below, or Refresh), while the memberships
// read still holds the pre-approval `[]` — nothing here re-asks it. A Continue that went to the root
// on that list was sent by the portal-entry gate to the org search, and when the fresh read landed
// the console shell rendered there, leaving the new Owner on "Find your organization". The approved
// card's Continue now asks for that read, holds while it is in flight without the charity, and then
// opens the organization deep link (`org/continue-into-organization.ts`, shared with the wizard's
// panel). Pinned through the real route tree in `router/claim-first-gate.test.tsx`.
//
// The root keeps `data-testid="page-claim-detail"`. Status pill: `claim-detail-status`.

import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { charityAdminListMyCharityClaimsOptions } from '@givmo/api-client/query';
import { isGivmoError, type CharityClaimRequestOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Icon,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import { ArrowLeft, CheckCircle2, FileSearch, Info, LayoutDashboard } from 'lucide-react';
import { extractClaims, formatEin } from './claim-decode';
import {
  CLAIM_TIER,
  claimReviewNote,
  claimStatusDescription,
  claimStatusLabel,
  claimStatusTone,
  isApprovedStatus,
  isPendingStatus,
  isRejectedStatus,
} from './claim-vocab';
import { CLAIM_ROUTES } from './claim-routes';
import { useContinueIntoOrganization } from '../../org/continue-into-organization';
import { DocumentUpload } from './document-upload';
import {
  EXTERNAL_STATUS_REFETCH_MS,
  ExternalStatusRefresh,
} from '../../lib/external-status-refresh';

const PREFIX = 'claim-detail';

export function ClaimDetailPage() {
  const navigate = useNavigate();
  // Mounted under the pathless `authenticated` layout → registered id `/authenticated/claims/$claimRequestId`.
  const { claimRequestId } = useParams({ from: '/authenticated/claims/$claimRequestId' });

  const claimsQuery = useQuery({
    ...charityAdminListMyCharityClaimsOptions(),
    select: (data: unknown) => extractClaims(data),
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const current = extractClaims(query.state.data).find((item) => item.id === claimRequestId);
      return current && isPendingStatus(current.status) ? EXTERNAL_STATUS_REFETCH_MS : false;
    },
  });

  const claim: CharityClaimRequestOut | undefined = useMemo(
    () => claimsQuery.data?.find((c) => c.id === claimRequestId),
    [claimsQuery.data, claimRequestId],
  );
  // CPA-WEB83, the claim page's Continue (see the header): asks for the memberships read once the
  // claim reads approved, holds until the list holds this charity, then opens its console.
  const continueInto = useContinueIntoOrganization(claim?.charity_id ?? null, {
    refresh: claim !== undefined && isApprovedStatus(claim.status),
  });

  if (claimsQuery.isPending) {
    return (
      <DetailShell title="Your claim">
        <Card className="px-24 py-24" testId={`${PREFIX}-loading`}>
          {/* M4: the loaded screen's shape — an identity line, a description line, and the action
              block beneath — so nothing re-flows when the claim resolves. */}
          <div className="flex flex-col gap-8">
            <Skeleton className="h-16 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
          <Skeleton className="mt-24 h-[120px] w-full rounded-xl" />
        </Card>
      </DetailShell>
    );
  }

  if (claimsQuery.isError) {
    return (
      <DetailShell title="Your claim">
        <EmptyState
          testId={`${PREFIX}-error`}
          icon={<Icon icon={Info} size={20} aria-hidden />}
          title="Couldn't load your claim"
          description={
            isGivmoError(claimsQuery.error)
              ? claimsQuery.error.message
              : 'We could not load this claim. Please try again.'
          }
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void claimsQuery.refetch()}
              testId={`${PREFIX}-error-retry`}
            >
              Try again
            </Button>
          }
        />
      </DetailShell>
    );
  }

  if (!claim) {
    return (
      <DetailShell title="Your claim">
        <EmptyState
          testId={`${PREFIX}-not-found`}
          icon={<Icon icon={FileSearch} size={20} aria-hidden />}
          title="Claim not found"
          description="We couldn't find this claim. It may belong to another account."
          action={
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void navigate({ to: CLAIM_ROUTES.claims })}
              testId={`${PREFIX}-back-to-claims`}
            >
              View my claims
            </Button>
          }
        />
      </DetailShell>
    );
  }

  const status = claim.status;
  const einDisplay = formatEin(claim.attested_ein);
  const isTier2 = claim.tier === CLAIM_TIER.tier2;
  const pending = isPendingStatus(status);
  const approved = isApprovedStatus(status);
  const rejected = isRejectedStatus(status);
  const orgName = claim.attested_legal_name || 'Your organization';
  const reviewNote = claimReviewNote(claim);
  const awaitingDocuments = isTier2 && pending;
  // The already-managed possibility explains a pending claim ONLY where the instant check should have
  // cleared and did not. A Tier-2 claim already has its own stated cause, so asserting takeover doubt
  // there adds a second, contradictory story about the same wait.
  const takeoverIsTheLiveExplanation = pending && !isTier2;

  const statusCard = (
    <Card testId={`${PREFIX}-summary`}>
      <CardBody className="flex flex-col gap-12 px-24 py-20">
        <div className="flex items-center justify-between gap-12">
          <div className="min-w-0">
            <div className="truncate font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              {orgName}
            </div>
            {einDisplay ? (
              <div className="mt-2 font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
                EIN <span className="tabular-nums">{einDisplay}</span>
              </div>
            ) : null}
          </div>
          <span {...testIdProp(`${PREFIX}-status`)}>
            <Badge tone={claimStatusTone(status)} dot>
              {claimStatusLabel(status)}
            </Badge>
          </span>
        </div>
        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {claimStatusDescription(status)}
        </p>
        {rejected && claim.reason ? (
          <div
            className="rounded-lg border border-danger-100 bg-danger-50 p-12 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-danger-700"
            {...testIdProp(`${PREFIX}-reason`)}
          >
            {claim.reason}
          </div>
        ) : null}
        {takeoverIsTheLiveExplanation ? (
          <p
            className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
            {...testIdProp(`${PREFIX}-takeover-note`)}
          >
            If this organization is already managed on Givmo, a reviewer checks your request before
            granting access. The fastest way in is to ask an existing admin to invite you.
          </p>
        ) : null}
      </CardBody>
    </Card>
  );

  return (
    <DetailShell
      title={awaitingDocuments ? 'Finish verifying your claim' : 'Your claim'}
      subtitle={
        awaitingDocuments
          ? `One document is all that's left for ${orgName}.`
          : 'Track your organization claim and upload any documents we need.'
      }
    >
      {/* ── The pending / Tier-2 path: the ACTION leads. ──────────────────────────────────── */}
      {awaitingDocuments ? (
        <>
          {reviewNote !== null ? (
            <div
              className="flex items-start gap-12 rounded-lg bg-surface-sunken p-16"
              {...testIdProp(`${PREFIX}-review-note`)}
            >
              <Icon icon={Info} size={18} className="mt-2 shrink-0 text-brand-700" aria-hidden />
              <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                {reviewNote}
              </p>
            </div>
          ) : null}
          <DocumentUpload claimRequestId={claim.id} />
        </>
      ) : null}

      {/* Approved → Continue into the new organization's console, once the memberships read holds it. */}
      {approved ? (
        <Card testId={`${PREFIX}-approved`}>
          <CardBody className="flex items-center justify-between gap-12 px-24 py-20">
            <div className="flex items-center gap-12">
              <Icon
                icon={CheckCircle2}
                size={20}
                className="shrink-0 text-success-700"
                aria-hidden
              />
              <span className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                You manage this organization. Set up payments and view your donations from your
                apps.
              </span>
            </div>
            <Button
              appearance="saas"
              tone="primary"
              size="sm"
              leadingIcon={<Icon icon={LayoutDashboard} size={14} aria-hidden />}
              loading={continueInto.pending}
              onClick={continueInto.onContinue}
              testId={`${PREFIX}-go-dashboard`}
            >
              Continue
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {statusCard}

      {pending ? (
        <ExternalStatusRefresh
          lastCheckedAt={claimsQuery.dataUpdatedAt}
          refreshing={claimsQuery.isFetching}
          onRefresh={() => void claimsQuery.refetch()}
          testId={`${PREFIX}-freshness`}
        />
      ) : null}
    </DetailShell>
  );
}

function DetailShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  return (
    <div className="flex w-full justify-center px-24 py-44" {...testIdProp('page-claim-detail')}>
      <div className="flex w-full max-w-[680px] flex-col gap-20">
        <div>
          <Button
            appearance="saas"
            tone="ghost"
            size="sm"
            leadingIcon={<Icon icon={ArrowLeft} size={14} aria-hidden />}
            onClick={() => void navigate({ to: CLAIM_ROUTES.claims })}
            testId={`${PREFIX}-back`}
          >
            All claims
          </Button>
        </div>
        <div className="flex flex-col gap-6">
          <h1 className="font-sans text-s24 font-w600 leading-h13 tracking-givmo text-text-strong">
            {title}
          </h1>
          {subtitle != null && (
            <p className="font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {subtitle}
            </p>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}
